/* eslint-disable no-console */

import React, {Component} from 'react';
import hoistStatics from 'hoist-non-react-statics';

const isTest = typeof window !== 'undefined' && window.isTest;
const isDev = process.env.NODE_ENV !== 'production';
// Make sure we're in an environment that can use the console
const canUseConsole = () =>
  typeof console !== 'undefined' &&
  typeof console.log === 'function' &&
  typeof console.groupCollapsed === 'function' &&
  typeof console.groupEnd === 'function';

const logOnce = (() => {
  const memo = {};
  const handleLogging = ({
    deprecatedModuleName,
    replacementModuleName,
    removalDate,
    endOfSupportDate,
  }) => {
    if (deprecatedModuleName in memo) {
      return;
    } else {
      if (canUseConsole()) {
        console.groupCollapsed(
          `%c 🚨 A deprecated component \`${deprecatedModuleName}\` has mounted 🚨`,
          'color: red; font-size: 16px;'
        );

        let message = `Please replace this component with ${replacementModuleName}.`;

        if (removalDate) {
          message += ` The \`${deprecatedModuleName}\` module will be removed from the codebase on ${removalDate}.\n`;
        }

        if (endOfSupportDate) {
          message += ` The \`${deprecatedModuleName}\` module will no longer be supported for anything less than a critical bug fix after ${endOfSupportDate}.\n`;
        }

        message +=
          '\nIf you have questions about this warning, ask in either #homebase-sf-help or #homebase-ent-help. \n';
        console.error(message);
        console.groupEnd();
      }
      memo[deprecatedModuleName] = true;
    }
  };

  return handleLogging;
})();

// Function that returns a HOC that is used to help deprecate Homebase components
const deprecatedComponent = ({
  deprecatedModuleName,
  replacementModuleName,
  removalDate,
  endOfSupportDate,
}) => {
  if (!deprecatedModuleName || !replacementModuleName) {
    throw new Error(
      'You must provide the name of the deprecated module and the name of the replacement module.'
    );
  }

  return (WrappedComponent) => {
    // If we're in a test environment - just return the wrapped component
    // Many tests break if we return the HOC
    if (isTest) {
      return WrappedComponent;
    } else {
      class WithComponentDeprecation extends Component {
        static defaultProps = {};
        componentDidMount() {
          // Only console.log the warnings in dev
          if (isDev) {
            logOnce({
              deprecatedModuleName,
              replacementModuleName,
              removalDate,
              endOfSupportDate,
            });
          }
        }

        render() {
          return <WrappedComponent {...this.props} />;
        }
      }

      /**
       * Hoist the "defaultProps" of the WrappedComponent. This was
       * causing issues for some users using this HOC who relied on
       * certain values that were defined in defaultProps
       */
      // $FlowIgnore: suppressing this defaultProps warning, since we're just checking to see if the field exists
      if (WrappedComponent.defaultProps) {
        WithComponentDeprecation.defaultProps = {
          ...WrappedComponent.defaultProps,
        };
      }

      hoistStatics(WithComponentDeprecation, WrappedComponent);
      return WithComponentDeprecation;
    }
  };
};

export default deprecatedComponent;
